import type { Family } from "@/generated/core/v1/family";
import { DEBT_KIND_LABEL } from "@/plan-logic/questionnaire/draft";

/**
 * "Fill in missing values": the values a household may leave blank as "not sure" (onboarding and
 * Update Data) that are still missing from its family document. Only the fields that offer "not
 * sure" count: the interest rate of each mortgage, loan, and credit card (`interest` unset). The
 * engine estimates them meanwhile; a rate the household confirmed in Sharpen's "What we estimated"
 * is stored, so it no longer counts. Selected from the document, not a rule. Shown as a
 * "check your data" row (`dataChecks.tsx`).
 */
export type MissingValue = { path: string; label: string };

export const FILL_MISSING_URL = "/app/update-data?open=debts";

const kindLabel = <T,>(options: [T, string][], v: T, fallback: string) => {
  const found = options.find(([k]) => k === v)?.[1];
  const label = !found || found === "Other" ? fallback : found;
  return label.charAt(0).toLowerCase() + label.slice(1);
};

export function missingValues(family: Family | undefined | null): MissingValue[] {
  if (!family) return [];
  const out: MissingValue[] = [];
  family.mortgages.forEach((m) => {
    if (m.interest !== undefined) return;
    // its stored name, as the Debt and Net Worth pages show it: the engine names an unnamed one by
    // its property ("Cottage Mortgage", "Lake house Mortgage")
    out.push({ path: `mortgages[${m.uuid}].interest`, label: m.name.trim() || "your mortgage" });
  });
  family.loans.forEach((l) => {
    if (l.interest !== undefined) return;
    out.push({
      path: `loans[${l.uuid}].interest`,
      label: l.name || `your ${kindLabel(DEBT_KIND_LABEL, l.type, "loan")}`,
    });
  });
  family.persons.forEach((p) =>
    p.creditCards.forEach((c) => {
      if (c.interest !== undefined) return;
      out.push({
        path: `persons[${p.uuid}].credit_cards[${c.uuid}].interest`,
        label: c.name || "your credit card",
      });
    }),
  );
  return out;
}

/** "... the interest rate on Visa, your car loan, and Home Mortgage ..." */
export function missingText(missing: MissingValue[]): string {
  const names = missing.map((m) => m.label);
  const list =
    names.length <= 2
      ? names.join(" and ")
      : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
  return `You weren’t sure of the interest rate on ${list}. Abi is estimating ${
    names.length === 1 ? "it" : "them"
  } until you fill ${names.length === 1 ? "it" : "them"} in.`;
}
